import { getKeycloakConfig } from './keycloakConfig';
import { clientDebugLog } from './clientDebugLog';

/**
 * Revokes a refresh (or offline) token via Keycloak's OAuth 2.0 revocation endpoint.
 * Safe for public clients — only `client_id` + token are required.
 *
 * @returns true when Keycloak accepted the revocation (2xx)
 */
export async function revokeKeycloakRefreshToken(refreshToken: string): Promise<boolean> {
	const trimmed = refreshToken.trim();
	if (!trimmed) {
		return false;
	}

	const { url, realm, clientId } = getKeycloakConfig();
	const endpoint = `${url.replace(/\/$/, '')}/realms/${realm}/protocol/openid-connect/revoke`;

	const body = new URLSearchParams({
		client_id: clientId,
		token: trimmed,
		token_type_hint: 'refresh_token',
	});

	const response = await fetch(endpoint, {
		method: 'POST',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body,
	});

	if (!response.ok) {
		clientDebugLog('warn', '[auth:logout] refresh token revoke failed', {
			status: response.status,
		});
		return false;
	}

	return true;
}

/** Best-effort revoke — never throws. */
export async function tryRevokeKeycloakRefreshToken(refreshToken: string | undefined): Promise<boolean> {
	if (!refreshToken) {
		return false;
	}

	try {
		return await revokeKeycloakRefreshToken(refreshToken);
	} catch (error) {
		clientDebugLog('warn', '[auth:logout] refresh token revoke error', {
			error: error instanceof Error ? error.message : String(error),
		});
		return false;
	}
}
